import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Scroll to the element matching location.hash (e.g. /#screens).
 * Needed because React Router Link updates the hash without native jump
 * when you are already on the same path.
 */
/** Scroll to `location.hash` on any route (e.g. Settings sections). */
export function useScrollToLocationHash(enabled = true): void {
  const { hash, pathname } = useLocation();

  useEffect(() => {
    if (!enabled || !hash) {
      return;
    }
    const id = decodeURIComponent(hash.replace(/^#/, ""));
    if (!id) {
      return;
    }

    const scroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };

    const frame = window.requestAnimationFrame(scroll);
    return () => window.cancelAnimationFrame(frame);
  }, [hash, pathname, enabled]);
}

export function useHashScroll(): void {
  const { hash, pathname } = useLocation();

  useEffect(() => {
    if (!hash || pathname !== "/") {
      return;
    }
    const id = decodeURIComponent(hash.replace(/^#/, ""));
    if (!id) {
      return;
    }

    const scroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };

    // Wait a frame so newly mounted sections exist.
    const frame = window.requestAnimationFrame(scroll);
    return () => window.cancelAnimationFrame(frame);
  }, [hash, pathname]);
}
